import { fetchAPI } from './core'

// Wizard API (Basic Mode)
export interface WizardCreateRequest {
  prompt: string
  org_id?: string
  mode?: 'basic' | 'advanced'
}

export interface WizardCreateResponse {
  project_id: string
  project_name: string
  language: string
  type: string
  agent_id: string
  message: string
}

export async function wizardCreate(data: WizardCreateRequest): Promise<WizardCreateResponse> {
  return fetchAPI('/wizard/create', {
    method: 'POST',
    body: JSON.stringify(data),
    skipAuth: true,
  })
}

export async function getWizardAgentStatus(agentId: string): Promise<{ agent_id: string; status: string; running: boolean }> {
  return fetchAPI(`/wizard/agent-status/${agentId}`, { skipAuth: true })
}

// Email-me-when-done for anonymous builds (PRD-GrowthLoops 1.2). Stores an
// email on an ownerless project; the API sends one link-back email when the
// agent completes.
export async function wizardNotify(projectId: string, email: string): Promise<{ message: string }> {
  return fetchAPI('/wizard/notify', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, email }),
    skipAuth: true,
  })
}

// Resume-link lookup for `/?build={projectID}` (from the build-ready email).
// Only anonymous (ownerless) projects resolve; owned or cleaned-up builds 404.
export interface WizardBuildInfo {
  project_id: string
  project_name: string
  language: string
  type: string
  status: string
}

export async function getWizardBuild(projectId: string): Promise<WizardBuildInfo> {
  return fetchAPI(`/wizard/build/${projectId}`, { skipAuth: true })
}

// Idea Estimator (Public — No Auth)
export interface EstimateComponent {
  name: string
  effort: 'low' | 'medium' | 'high'
  description: string
}

export interface EstimateTechStack {
  frontend: string[]
  backend: string[]
  infrastructure: string[]
  suggested_hosting: string
}

export interface EstimateResult {
  complexity: string
  complexity_score: number
  complexity_reasoning: string
  tech_stack: EstimateTechStack
  components: EstimateComponent[]
  solo_dev_estimate: string
  factory_estimate: string
  architecture_summary: string
}

export interface EstimateResponse {
  id: string
  idea: string
  estimate: EstimateResult
  created_at: string
  share_url: string
}

export async function createEstimate(idea: string): Promise<EstimateResponse> {
  return fetchAPI('/estimate', {
    method: 'POST',
    body: JSON.stringify({ idea }),
    skipAuth: true,
  })
}

export async function getEstimate(id: string): Promise<EstimateResponse> {
  return fetchAPI(`/estimate/${id}`, { skipAuth: true })
}

// Conversational Estimator (multi-turn). Works anonymously or signed-in; the API
// gates lifetime replies by tier (3 anonymous / 10 free / unlimited paid).

export interface EstimateChatSummary {
  title: string
  project_type: string
  complexity: string
  complexity_score: number
  complexity_reasoning: string
  key_considerations: string[]
  tech_stack: EstimateTechStack
  workstreams: EstimateComponent[]
  solo_estimate: string
  team_estimate: string
  factory_fit: string
  architecture_summary: string
}

export interface EstimateChatResponse {
  conversation_id: string
  reply: string
  summary: EstimateChatSummary | null
  replies_used: number
  replies_limit: number // -1 = unlimited
}

export interface EstimateChatMessage {
  id: string
  conversation_id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

export interface EstimateConversation {
  id: string
  title: string
  summary: EstimateChatSummary | null
  messages?: EstimateChatMessage[]
  created_at: string
  updated_at: string
}

// sendEstimateMessage sends one turn. Pass an empty conversationId to start a new
// conversation; reuse the returned conversation_id to continue. Token is attached
// automatically when signed in (no skipAuth) so higher tiers get higher caps.
export async function sendEstimateMessage(
  conversationId: string,
  message: string,
): Promise<EstimateChatResponse> {
  return fetchAPI('/estimator/chat', {
    method: 'POST',
    body: JSON.stringify({ conversation_id: conversationId, message }),
  })
}

export async function getEstimateConversation(id: string): Promise<EstimateConversation> {
  return fetchAPI(`/estimator/conversations/${id}`)
}

export async function listEstimateConversations(): Promise<EstimateConversation[]> {
  const resp = await fetchAPI<{ conversations: EstimateConversation[] }>('/estimator/conversations')
  return resp.conversations || []
}

// Estimate funnel events (PRD-IdeaEstimator Ph3). Public; `signed_up` is only
// accepted from a signed-in client so it can be attributed to the new user.
export type EstimateEventType = 'shared' | 'cta_clicked' | 'signed_up'
export type EstimateCTA = 'build' | 'share' | 'retry'

export async function recordEstimateEvent(
  estimateId: string,
  event: { event_type: EstimateEventType; cta?: EstimateCTA; referrer?: string },
): Promise<void> {
  await fetchAPI(`/estimate/${encodeURIComponent(estimateId)}/events`, {
    method: 'POST',
    body: JSON.stringify(event),
  })
}

// Session-scoped estimate reference: set when a visitor engages with a card
// (build CTA) so a later signup in the same tab can be attributed to it.
export const ESTIMATE_REF_KEY = 'factory_estimate_ref'

// Public example gallery: admin-featured estimate cards (no conversation).
export interface EstimateExample {
  id: string
  title: string
  summary: EstimateChatSummary | null
  created_at: string
}

export async function getEstimateExamples(): Promise<EstimateExample[]> {
  const resp = await fetchAPI<{ examples: EstimateExample[] }>('/estimate/examples', { skipAuth: true })
  return resp.examples || []
}
